import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import {
  type Control,
  type DefaultValues,
  type FieldErrors,
  type FieldValues,
  type UseFormGetValues,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormTrigger,
  type UseFormWatch,
  useForm,
} from 'react-hook-form';
import type { ZodType } from 'zod';

interface UseZodFormProps<TData extends FieldValues> {
  // biome-ignore lint/suspicious/noExplicitAny: necessary to not have complex zod type
  schema: ZodType<TData, unknown, any>;
  defaultValues: DefaultValues<TData>;
  onSubmit?: (data: TData) => void;
}

export interface FormProps<TData extends FieldValues> {
  control: Control<TData>;
  register: UseFormRegister<TData>;
  errors: FieldErrors<TData>;
}

export interface ZodForm<TData extends FieldValues> {
  formData: FormProps<TData>;
  submit: () => void;
  resetForm: () => void;
  watch: UseFormWatch<TData>;
  setValue: UseFormSetValue<TData>;
  getValues: UseFormGetValues<TData>;
  trigger: UseFormTrigger<TData>;
}

export function useZodForm<TData extends FieldValues>(
  props: UseZodFormProps<TData>
): ZodForm<TData> {
  const { schema, defaultValues, onSubmit = () => {} } = props;

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { errors },
    reset,
  } = useForm<TData>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues,
  });

  const resetForm = useCallback(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  return {
    formData: {
      control,
      register,
      errors,
    },
    submit: handleSubmit(onSubmit),
    resetForm,
    watch,
    setValue,
    getValues,
    trigger,
  };
}
